/**
 * Pipeline Swimlanes View Component
 * Kanban board grouped by OEM, Region, or Assignee
 */
import React, { useState, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { ChevronDown, ChevronRight, Building2, Globe, User } from 'lucide-react';
import { formatCompactCurrency } from '../../api/pipeline';
import KanbanCard from './KanbanCard';

// Stage configuration
const STAGES = [
  { id: 'target', name: 'Target', color: 'bg-blue-500' },
  { id: 'qualified', name: 'Qualified', color: 'bg-purple-500' },
  { id: 'rfq', name: 'RFQ', color: 'bg-indigo-500' },
  { id: 'quoted', name: 'Quoted', color: 'bg-cyan-500' },
  { id: 'awarded', name: 'Awarded', color: 'bg-emerald-500' },
  { id: 'sourced', name: 'Sourced', color: 'bg-green-500' },
  { id: 'lost', name: 'Lost', color: 'bg-slate-400' },
];

const KanbanSwimlanes = ({
  opportunities,
  groupBy,
  onCardClick,
  onStageChange,
  onArchive,
  onDragEnd,
}) => {
  const [collapsedLanes, setCollapsedLanes] = useState({});

  // Get grouping icon
  const getGroupIcon = () => {
    switch (groupBy) {
      case 'oem': return Building2;
      case 'region': return Globe;
      case 'assignee': return User;
      default: return Building2;
    }
  };

  const GroupIcon = getGroupIcon();

  // Group opportunities into swimlanes
  const swimlanes = useMemo(() => {
    const lanes = {};
    const ungroupedKey = groupBy === 'assignee' ? 'Unassigned' : 'Other';

    // Flatten all opportunities
    Object.entries(opportunities).forEach(([stage, opps]) => {
      opps.forEach(opp => {
        let groupKey;
        
        switch (groupBy) {
          case 'oem':
            groupKey = opp.oem || ungroupedKey;
            break;
          case 'region':
            groupKey = opp.region || ungroupedKey;
            break;
          case 'assignee':
            groupKey = opp.assigned_to?.name || ungroupedKey;
            break;
          default:
            groupKey = ungroupedKey;
        }

        if (!lanes[groupKey]) {
          lanes[groupKey] = {
            name: groupKey,
            stages: {},
            totalRevenue: 0,
            totalCount: 0,
          };
          // Initialize all stages
          STAGES.forEach(s => {
            lanes[groupKey].stages[s.id] = [];
          });
        }

        lanes[groupKey].stages[stage].push(opp);
        lanes[groupKey].totalRevenue += opp.revenue_potential_cents || 0;
        lanes[groupKey].totalCount += 1;
      });
    });

    // Sort lanes by total revenue (descending)
    return Object.values(lanes).sort((a, b) => {
      if (a.name === ungroupedKey) return 1;
      if (b.name === ungroupedKey) return -1;
      return b.totalRevenue - a.totalRevenue;
    });
  }, [opportunities, groupBy]);

  // Toggle lane collapse
  const toggleLane = (laneName) => {
    setCollapsedLanes(prev => ({
      ...prev,
      [laneName]: !prev[laneName]
    }));
  };

  // Handle drag end within swimlanes
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    // Parse the droppableId to get lane and stage
    const [, destStage] = result.destination.droppableId.split('::');
    
    if (onDragEnd) {
      // Reconstruct result with just the stage for the main handler
      onDragEnd({
        ...result,
        source: {
          ...result.source,
          droppableId: result.source.droppableId.split('::')[1]
        },
        destination: {
          ...result.destination,
          droppableId: destStage
        }
      });
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="space-y-4">
        {swimlanes.map((lane) => {
          const isCollapsed = collapsedLanes[lane.name];
          
          return (
            <div 
              key={lane.name}
              className="bg-white rounded-lg border border-slate-200 overflow-hidden"
            >
              {/* Lane Header */}
              <div 
                className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors"
                onClick={() => toggleLane(lane.name)}
              >
                <div className="flex items-center gap-3">
                  {isCollapsed ? (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                  <GroupIcon className="w-5 h-5 text-slate-500" />
                  <h3 className="font-semibold text-slate-900">{lane.name}</h3>
                  <span className="text-sm text-slate-500">
                    ({lane.totalCount} opportunities)
                  </span>
                </div>
                <div className="text-sm font-medium text-slate-700">
                  {formatCompactCurrency(lane.totalRevenue)}
                </div>
              </div>

              {/* Lane Content */}
              {!isCollapsed && (
                <div className="flex overflow-x-auto p-4 gap-4">
                  {STAGES.map((stage) => {
                    const stageOpps = lane.stages[stage.id] || [];
                    const droppableId = `${lane.name}::${stage.id}`;

                    return (
                      <div 
                        key={stage.id}
                        className="flex-shrink-0 w-72"
                      >
                        {/* Stage Header */}
                        <div className="flex items-center gap-2 mb-2 px-2">
                          <span className={`w-2 h-2 rounded-full ${stage.color}`} />
                          <span className="text-sm font-medium text-slate-700">
                            {stage.name}
                          </span>
                          <span className="text-xs text-slate-500">
                            ({stageOpps.length})
                          </span>
                        </div>

                        {/* Stage Column */}
                        <Droppable droppableId={droppableId}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className={`
                                min-h-[100px] p-2 rounded-lg border-2 border-dashed
                                transition-colors
                                ${snapshot.isDraggingOver 
                                  ? 'border-blue-400 bg-blue-50' 
                                  : 'border-slate-200 bg-slate-50'}
                              `}
                            >
                              {stageOpps.length === 0 ? (
                                <div className="flex items-center justify-center h-20 text-xs text-slate-400">
                                  Drop here
                                </div>
                              ) : (
                                stageOpps.map((opp, index) => (
                                  <Draggable
                                    key={opp.id}
                                    draggableId={opp.id}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                      >
                                        <KanbanCard
                                          opportunity={opp}
                                          onClick={() => onCardClick(opp)}
                                          isDragging={snapshot.isDragging}
                                          onStageChange={onStageChange}
                                          onArchive={onArchive}
                                          compact={true}
                                        />
                                      </div>
                                    )}
                                  </Draggable>
                                ))
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {swimlanes.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <p>No opportunities to display</p>
          </div>
        )}
      </div>
    </DragDropContext>
  );
};

export default KanbanSwimlanes;
